import React, { useMemo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { motion } from 'framer-motion'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { TaskEntry } from '../TaskEntry'
import { SortableTaskEntry } from './SortableTaskEntry'
import { NewTaskDropGap } from '../NewTaskDrag'
import { LIST_ROW_GAP } from '../../utils/newTaskDropTarget'
import { SelectionHull, useRestAfterDrop, useSelectionHullResting } from '../SelectionHull'
import type { TaskCreationData, TaskEntryData } from '../TaskEntry'
import { useTaskListInteraction } from '../../hooks/useTaskListInteraction'
import type { TaskListProps } from './TaskList.types'
import './TaskList.scss'
import { TaskRowDataScope } from '../TaskRowData'
import { LazyRowScope } from '../LazyRows'
import { MultiSelectionContext } from '../../utils/multiSelection'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { withFocusSeconds, type TaskWithFocus } from '../../utils/withFocusSeconds'
import { ListFocusContext } from '../../utils/listFocus'

/**
 * Eine Zeile, die gerade in der Liste erscheint: Sie fährt auf und blendet ein.
 *
 * **Nach dem Auffahren wieder `overflow: visible`, per Zustand und nicht per `transitionEnd`**
 * (gemeldet 28.09.2026). Framer 12.43 schreibt einen `transitionEnd`-Wert erst beim nächsten
 * React-Render ins DOM — rendert die Liste danach nicht mehr, bleibt `hidden` stehen (im Browser
 * gemessen). Das schnitt damals die 4-px-Brücke zwischen zwei markierten Zeilen ab; seit #293 malt
 * die Auswahl-Hülle außerhalb der Zeile, beschnitten würden heute Schatten und Menüs der Zeile.
 */
/** So lange nach dem Loslassen gilt eine gezogene Aufgabe nicht als neu erscheinend. */
const DROP_SETTLE_MS = 2000

const EnteringRow: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [opened, setOpened] = useState(false)
  return (
    <motion.div
      initial={{ height: 0, overflow: 'hidden' as const }}
      animate={opened ? { height: 'auto', overflow: 'visible' as const } : { height: 'auto' }}
      transition={{ height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } }}
      onAnimationComplete={() => setOpened(true)}
    >
      <motion.div
        initial={{ opacity: 0, y: -8, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: 0.25,
          delay: 0.05,
          ease: [0.4, 0, 0.2, 1],
        }}
      >
        {children}
      </motion.div>
    </motion.div>
  )
}

/**
 * Reusable task list with built-in selection, keyboard navigation, multi-select,
 * and inline editing via double-click.
 *
 * Wraps TaskEntry + useTaskListInteraction so consumers don't need to wire up
 * the hook manually. Supports click, Cmd/Ctrl+Click, Shift+Click, arrow keys,
 * Space (toggle), Enter (open), Escape (clear), and double-click (edit).
 *
 * @example
 * <TaskList
 *   tasks={[{ id: '1', title: 'Buy milk', completed: false }]}
 *   onToggleComplete={(ids) => toggleTasks(ids)}
 *   onUpdateTask={(id, data) => updateTask(id, data)}
 * />
 */
export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  onToggleComplete,
  onDelete,
  onDuplicate,
  onContextMenuBadgeUpdate,
  getContextMenuMarks,
  menuShortcut, onMenuShortcutClose,
  onSelectionChange,
  onOpen,
  onUpdateTask,
  onUpdateTaskAndCreateBelow,
  onNavigateEdit,
  checkKind,
  checkVariant,
  menuKind,
  onShowInProject,
  onReopen,
  projectBadgePlacement,
  recurrenceDisplay,
  hidePlay,
  onPlay,
  excludeTaskId,
  stateKey,
  handleRef,
  autoSelectId,
  autoCollapseId,
  onEscapeTop,
  onEscapeBottom,
  externalSelection,
  inlineCreation,
  newTaskGap = null,
  skipSortableContext,
  draggedIds,
  nestTarget,
  activeDragId,
  dragCount,
  isCrossSegmentDrag,
  skipEntrance,
  initialEnteringId,
  inProjectContext,
}) => {
  const sichtbareTasks = useMemo(
    () => excludeTaskId ? tasks.filter(t => t.id !== excludeTaskId) : tasks,
    [tasks, excludeTaskId],
  )

  const taskIds = useMemo(() => sichtbareTasks.map(t => t.id), [sichtbareTasks])

  /*
    **Die Fokuszeit je Zeile** (3e) — hier und nicht in jeder Seite.

    `useFocusSeconds` ist listenförmig gebaut: EINE Live-Query für alle
    Kennungen zusammen, nicht eine je Zeile (`feedback_performance_patterns`).
    Damit trägt jede Liste die Zahl, ohne dass Projekt, Demnächst, Kurz notiert
    und Geschafft sie einzeln durchfädeln — fünf Stellen, die auseinanderlaufen
    könnten, werden zu einer.

    Nur **gebuchte** Abschnitte, kein laufender: Während eines Fokus sieht
    niemand eine Liste (CONTEXT.md).

    Solange die Abfrage lädt (`undefined`), wird das Feld NICHT gesetzt — das
    ursprüngliche Array reicht unverändert durch, und niemand zeichnet neu, nur
    weil eine Zahl unterwegs ist.
  */
  const focusSecondsByTask = useDataSource().useFocusSeconds(taskIds)

  /*
    **Nur die Zeile neu bauen, deren Zeit sich geändert hat** (gemessen
    21.09.2026, `render-audit`). Hier stand ein `map`, das für JEDE Zeile ein
    neues Objekt schuf, sobald die Live-Abfrage feuerte — und sie feuert mit
    einer neuen Map, auch wenn keine Zahl anders ist. Damit rendern alle
    Zeilen neu, und das `React.memo` an `TaskEntry` greift bei keiner.
  */
  const focusCache = useRef(new WeakMap<TaskEntryData, TaskWithFocus<TaskEntryData>>())
  const filteredTasks = useMemo(
    () => withFocusSeconds(sichtbareTasks, focusSecondsByTask, focusCache.current),
    [sichtbareTasks, focusSecondsByTask],
  )

  // ── Animation readiness for exit animations ──
  //
  // animReadyRef gates the exit-animation setup in wrappedOnDelete so that
  // delete animations only fire after tasks have settled (50ms after mount /
  // tab switch). This prevents race conditions during the initial render.
  const animReadyRef = useRef(false)
  const prevStKeyRef = useRef(stateKey)
  const [exitTick, setExitTick] = useState(0)

  // ── Exit animation state ──
  // When tasks are deleted, we cache their data and keep them in the display
  // list with a fade-out + height-collapse animation before removing them.
  const exitingIdsRef = useRef<Set<string>>(new Set())
  const exitingDataRef = useRef<Map<string, TaskEntryData>>(new Map())
  const exitOriginalOrderRef = useRef<string[]>([])
  const filteredTasksRef = useRef(filteredTasks)
  filteredTasksRef.current = filteredTasks

  // ── Entrance animation state ──
  // Tracks IDs that just appeared in the list so they can fade in.
  const enteringIdsRef = useRef<Set<string>>(new Set())
  const prevTaskIdsRef = useRef<Set<string>>(new Set())
  // Tracks ALL excluded task IDs so morph→real swap skips entrance animation.
  // A Set (not a single ref) because rapid CMD+Enter can cycle through multiple
  // excludeTaskIds before previous ones transition to visible.
  const everExcludedRef = useRef<Set<string>>(new Set())
  // Tracks previous initialEnteringId to detect when it transitions from unset to set
  const prevInitialEnteringRef = useRef<string | undefined>(undefined)
  /*
    **Eine abgelegte Aufgabe fährt nicht noch einmal auf** (Sven, 05.10.2026, im Web gesehen). Landet
    sie in einem anderen Abschnitt, kann sie dort für einen Moment fehlen — die Vorschau des Ablegens
    ist schon weg, die Datenbank noch nicht nachgezogen. Kam sie dann an, galt sie als neu, und die
    Hülle von `EnteringRow` (`overflow: hidden`) schnitt den Pop samt Schatten ab, bis sie aufging.
    Ihr Auftritt ist der Pop. Alle Listen sehen dieselben `draggedIds` — auch die Ziel-Liste.
  */
  const recentlyDraggedRef = useRef<Set<string>>(new Set())
  if (activeDragId) recentlyDraggedRef.current.add(activeDragId)
  draggedIds?.forEach(id => recentlyDraggedRef.current.add(id))
  useEffect(() => {
    if (activeDragId || draggedIds || recentlyDraggedRef.current.size === 0) return
    const timer = setTimeout(() => recentlyDraggedRef.current.clear(), DROP_SETTLE_MS)
    return () => clearTimeout(timer)
  }, [activeDragId, draggedIds])

  if (prevStKeyRef.current !== stateKey) {
    prevStKeyRef.current = stateKey
    animReadyRef.current = false
    exitingIdsRef.current.clear()
    exitingDataRef.current.clear()
    enteringIdsRef.current.clear()
    prevTaskIdsRef.current.clear()
    everExcludedRef.current.clear()
    prevInitialEnteringRef.current = undefined
  }

  // ── Entrance animation detection (synchronous, before render) ──
  // Compare current task IDs with previous render to find newly appeared tasks.
  // Only fire after the initial settle period (animReadyRef) and when there were
  // previous tasks (to avoid animating all tasks on mount).
  // Skip tasks that were just un-excluded (morph→real task swap — no animation needed).
  // initialEnteringId: animate this task even on first mount (e.g. first unsectioned task).
  const currentTaskIdSet = useMemo(() => new Set(filteredTasks.map(t => t.id)), [filteredTasks])
  // Detect whether prev→current is an incremental change (some IDs overlap)
  // or a complete set replacement (no overlap, e.g. stale crossfade → real data on tab switch).
  const hasOverlap = prevTaskIdsRef.current.size > 0
    && [...currentTaskIdSet].some(id => prevTaskIdsRef.current.has(id))
  // Track all excluded IDs so they never get entrance animations
  if (excludeTaskId) {
    everExcludedRef.current.add(excludeTaskId)
  }
  if (animReadyRef.current && hasOverlap && !skipEntrance) {
    for (const id of currentTaskIdSet) {
      if (!prevTaskIdsRef.current.has(id) && !everExcludedRef.current.has(id) && !recentlyDraggedRef.current.has(id)) {
        enteringIdsRef.current.add(id)
      }
    }
  }
  // Animate the very first task in an empty list (e.g. first unsectioned task via Cmd+Enter).
  // Two cases:
  // 1. Same render: task + initialEnteringId arrive together, prevTaskIdsRef is empty → animate.
  // 2. Late render: initialEnteringId arrives one render after the task (useEffect timing from
  //    pendingInlineTarget). Detect this by comparing with prevInitialEnteringRef.
  // Treat a complete set replacement (no overlap) the same as empty — stale crossfade data
  // shouldn't block the animation for a genuinely new first task.
  const effectivelyEmpty = prevTaskIdsRef.current.size === 0 || !hasOverlap
  const initialEnteringIsNew = initialEnteringId !== prevInitialEnteringRef.current
  if (initialEnteringId && currentTaskIdSet.has(initialEnteringId)
    && !everExcludedRef.current.has(initialEnteringId)
    && (effectivelyEmpty || initialEnteringIsNew)) {
    enteringIdsRef.current.add(initialEnteringId)
  }
  prevInitialEnteringRef.current = initialEnteringId
  prevTaskIdsRef.current = currentTaskIdSet

  // ── Wrapped onDelete with exit animation ──
  // Caches task data at original positions, then calls parent onDelete immediately.
  // The cached data stays in the display list for 250ms with a fade-out animation.
  const wrappedOnDelete = useCallback((ids: Set<string>) => {
    if (!onDelete) return

    if (animReadyRef.current) {
      const current = filteredTasksRef.current
      exitOriginalOrderRef.current = current.map(t => t.id)
      ids.forEach(id => {
        const task = current.find(t => t.id === id)
        if (task) {
          exitingIdsRef.current.add(id)
          exitingDataRef.current.set(id, task)
        }
      })
      setExitTick(n => n + 1)

      const exitIds = [...ids]
      setTimeout(() => {
        exitIds.forEach(id => {
          exitingIdsRef.current.delete(id)
          exitingDataRef.current.delete(id)
        })
        setExitTick(n => n + 1)
      }, 300)
    }

    onDelete(ids)
  }, [onDelete])

  const {
    selectedIds,
    focusedIndex,
    listProps,
    getItemProps,
    handleSelect,
    handleDoubleClick,
    editingTaskId,
    autoCollapsingTaskId,
    cancelEditing,
    clearSelection,
    selectTask,
    selectTasks,
    completeSelection,
    deleteSelection,
    focusFirst,
    focusLast,
    focusList,
    restoreListFocus,
  } = useTaskListInteraction(taskIds, {
    onToggleComplete,
    onDelete: wrappedOnDelete,
    onOpen,
    onEscapeTop,
    onEscapeBottom,
    stateKey,
    autoSelectId,
    autoCollapseId,
    externalSelection,
  })

  // Dasselbe Muster wie bei `selectedIds`: Der Griff liest den Fokus
  // synchron, ohne dass die Zuweisung bei jedem Pfeiltastendruck neu läuft.
  const focusedIndexRef = useRef(focusedIndex)
  focusedIndexRef.current = focusedIndex
  const taskIdsRef = useRef(taskIds)
  taskIdsRef.current = taskIds

  // Keep a live ref to selectedIds so handleRef.getSelection() returns the
  // current value synchronously — parents read this from right-click
  // handlers that need to know whether the clicked task is part of a
  // multi-selection.
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds

  // Expose clearSelection + selection snapshot to parent via handleRef
  useEffect(() => {
    if (handleRef) {
      handleRef.current = {
        clearSelection,
        selectTask,
        selectTasks,
        focusFirst,
        focusLast,
        focusList,
        getSelection: () => selectedIdsRef.current,
        getFocusedTaskId: () => focusedIndexRef.current !== null ? taskIdsRef.current[focusedIndexRef.current] ?? null : null,
        completeSelection,
        deleteSelection,
      }
    }
    return () => { if (handleRef) handleRef.current = null }
  }, [handleRef, clearSelection, selectTask, selectTasks, focusFirst, focusLast, focusList, completeSelection, deleteSelection])

  const onSelectionChangeRef = useRef(onSelectionChange)
  onSelectionChangeRef.current = onSelectionChange
  const focusedTaskId = focusedIndex !== null ? taskIds[focusedIndex] ?? null : null
  useEffect(() => {
    onSelectionChangeRef.current?.({ selectedIds, focusedTaskId })
  }, [selectedIds, focusedTaskId])

  const handleSubmitEdit = useCallback((taskId: string, data: TaskCreationData) => {
    onUpdateTask?.(taskId, data)
    cancelEditing()
  }, [onUpdateTask, cancelEditing])

  const handleSubmitEditAndCreateBelow = useCallback((taskId: string, data: TaskCreationData) => {
    onUpdateTaskAndCreateBelow?.(taskId, data)
    cancelEditing()
  }, [onUpdateTaskAndCreateBelow, cancelEditing])

  const handleCancelEdit = useCallback(() => {
    cancelEditing()
  }, [cancelEditing])

  // ── Stable per-row callbacks ──
  // Each SortableTaskEntry below is wrapped in React.memo (and forwards most
  // props into a memoized TaskEntry). Passing fresh arrow functions per row
  // would defeat both memos — any TaskList re-render (e.g. editingTaskId
  // flipping on one row) would bleed into every other row and re-run the
  // entire TaskEntry render path. useCallback here converts the bulk
  // signatures (Set<string>) into the single-id signature TaskEntry expects,
  // while keeping the reference stable across renders.
  const handleItemToggleComplete = useCallback(
    (id: string) => onToggleComplete(new Set([id])),
    [onToggleComplete],
  )
  const handleItemDelete = useCallback(
    (id: string) => wrappedOnDelete(new Set([id])),
    [wrappedOnDelete],
  )
  /*
    Der Griff der Seite hängt dort an der Auswahl und wechselt mit ihr
    (gemessen 21.09.2026: `onDuplicate×33`). Über den Ref gelesen bleibt
    DIESER Griff stabil — gebraucht wird er ohnehin erst im Menü.
  */
  const onDuplicateRef = useRef(onDuplicate)
  onDuplicateRef.current = onDuplicate
  const handleItemDuplicate = useCallback((id: string) => onDuplicateRef.current?.(id), [])

  // Enable exit animations after tasks have settled for the current stateKey
  useEffect(() => {
    if (animReadyRef.current) return
    if (filteredTasks.length === 0) return
    const timer = setTimeout(() => {
      animReadyRef.current = true
    }, 50)
    return () => clearTimeout(timer)
  }, [filteredTasks.length, stateKey])

  // ── Scroll on expand ──
  // When a task expands near the bottom edge of the scroll container, we may
  // need extra space to scroll to. A temporary margin-bottom on the list
  // reserves that space (removed once expanded content provides enough height).
  // A smooth scroll after the expand animation brings the task into view.
  const listElRef = listProps.ref as React.RefObject<HTMLDivElement>

  // Track previous editingTaskId so we can smooth-scroll the closed task into view.
  const prevEditingIdRef = useRef<string | null>(null)
  useEffect(() => {
    prevEditingIdRef.current = editingTaskId
  })

  // Reserve scroll room for bottom-edge items (before paint, no scroll change).
  // On open: set marginBottom synchronously (useLayoutEffect) so scroll has room.
  // On close: defer removal until after the Framer Motion collapse animation (150ms)
  // to prevent a scroll jump ("snap") while the task card is still shrinking.
  const hadMarginRef = useRef(false)

  useLayoutEffect(() => {
    if (!editingTaskId) return
    const listEl = listElRef.current
    if (!listEl) return

    const wrapper = listEl.querySelector(`[data-task-id="${editingTaskId}"]`) as HTMLElement
    if (!wrapper) return

    const scrollContainer = getScrollContainer(listEl)
    if (!scrollContainer) return

    const EXPAND_DELTA = 150
    const wrapperRect = wrapper.getBoundingClientRect()
    const scrollRect = scrollContainer.getBoundingClientRect()
    const overflow = (wrapperRect.bottom + EXPAND_DELTA) - scrollRect.bottom

    if (overflow <= 0) return

    const remainingScroll = scrollContainer.scrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight
    if (remainingScroll >= overflow) return

    const marginNeeded = overflow - remainingScroll
    // Disable any in-flight margin transition so the scroll-room reserve is instant.
    listEl.style.transition = 'none'
    listEl.style.marginBottom = `${marginNeeded}px`
    hadMarginRef.current = true
  }, [editingTaskId, listElRef])

  // Margin cleanup: delegate to a CSS transition on the compositor instead of
  // running a 500ms rAF loop on the main thread (which conflicted with Framer
  // Motion's collapse animation and caused frame drops).
  useEffect(() => {
    if (editingTaskId || !hadMarginRef.current) return
    hadMarginRef.current = false
    const listEl = listElRef.current
    if (!listEl) return
    const currentMargin = parseFloat(listEl.style.marginBottom) || 0
    if (currentMargin <= 0) {
      listEl.style.marginBottom = ''
      listEl.style.transition = ''
      return
    }

    // Cubic ease-in-out matches the feel of the previous JS animation.
    listEl.style.transition = 'margin-bottom 500ms cubic-bezier(0.65, 0, 0.35, 1)'
    // rAF ensures the browser commits the current marginBottom as the transition's
    // starting value before we set the target, so the animation runs reliably.
    const rafId = requestAnimationFrame(() => {
      listEl.style.marginBottom = '0px'
    })

    const cleanupTimer = setTimeout(() => {
      listEl.style.transition = ''
      listEl.style.marginBottom = ''
    }, 520)

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(cleanupTimer)
      // Revoke inline transition so a subsequent useLayoutEffect write is instant.
      listEl.style.transition = ''
    }
  }, [editingTaskId, listElRef])

  // Smooth scroll task into view — starts shortly after expand begins
  // so the element has enough height for scrollIntoView to work correctly.
  useEffect(() => {
    // On expand: scroll twice — once early to start scrolling in parallel with
    // the expand animation, and once after it completes to ensure full visibility.
    if (editingTaskId) {
      const listEl = listElRef.current
      if (!listEl) return
      const scrollTo = () => {
        const wrapper = listEl.querySelector(`[data-task-id="${editingTaskId}"]`) as HTMLElement
        wrapper?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
      const t1 = setTimeout(scrollTo, 50)   // early: starts scroll during animation
      const t2 = setTimeout(scrollTo, 300)   // final: corrects for full expanded height
      return () => { clearTimeout(t1); clearTimeout(t2) }
    }

    // On collapse: smooth-scroll the just-closed task into view.
    // Without this, the height collapse + margin removal causes an abrupt jump.
    const closedId = prevEditingIdRef.current
    if (!closedId) return

    const timer = setTimeout(() => {
      const listEl = listElRef.current
      if (!listEl) return
      const wrapper = listEl.querySelector(`[data-task-id="${closedId}"]`) as HTMLElement
      wrapper?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }, 200) // after Framer Motion collapse (150ms), before margin removal (350ms)
    return () => clearTimeout(timer)
  }, [editingTaskId, listElRef])

  /*
    **Eine eingehängte Zeile bleibt stehen, bis ihre Stelle zugefahren ist** (28.09.2026).
    Die Datenbank nimmt sie nach wenigen Millisekunden aus der Liste; wäre sie dann weg,
    spränge alles darunter samt der aufklappenden Karte um eine Zeilenhöhe. Solange der Hook
    sie in `draggedIds` führt (nach dem Loslassen, ohne `activeDragId`), steht sie an ihrem
    alten Platz weiter — dasselbe Element, damit der Übergang nicht neu beginnt.
  */
  const shownRef = useRef<readonly TaskEntryData[]>([])
  const leavingIds = !activeDragId && draggedIds ? draggedIds : null
  const heldIds = useMemo(() => {
    if (!leavingIds) return null
    const present = new Set(filteredTasks.map(t => t.id))
    const held = new Set(shownRef.current.filter(t => leavingIds.has(t.id) && !present.has(t.id)).map(t => t.id))
    return held.size > 0 ? held : null
  }, [filteredTasks, leavingIds])
  const baseTasks = useMemo(() => {
    if (!heldIds) return filteredTasks
    const present = new Map(filteredTasks.map(t => [t.id, t]))
    const result: TaskEntryData[] = []
    for (const t of shownRef.current) {
      const current = heldIds.has(t.id) ? t : present.get(t.id)
      if (current) result.push(current)
      present.delete(t.id)
    }
    return [...result, ...present.values()]
  }, [filteredTasks, heldIds])
  shownRef.current = baseTasks

  // ── Display list (active + exiting tasks in original order) ──
  const hasExiting = exitingIdsRef.current.size > 0

  const displayList = useMemo(() => {
    if (!hasExiting) {
      return baseTasks.map(t => ({ task: t, exiting: false }))
    }

    const activeMap = new Map(baseTasks.map(t => [t.id, t]))
    const result: { task: TaskEntryData; exiting: boolean }[] = []
    const included = new Set<string>()

    // Follow pre-deletion order to preserve positions
    for (const id of exitOriginalOrderRef.current) {
      if (exitingIdsRef.current.has(id)) {
        const data = exitingDataRef.current.get(id)
        if (data) {
          result.push({ task: data, exiting: true })
          included.add(id)
        }
      } else if (activeMap.has(id)) {
        result.push({ task: activeMap.get(id)!, exiting: false })
        included.add(id)
      }
    }

    // Append any tasks not in the original order (e.g. newly created)
    for (const t of baseTasks) {
      if (!included.has(t.id)) {
        result.push({ task: t, exiting: false })
      }
    }

    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTasks, exitTick])

  /*
    **Was die Auswahl-Hülle zeigt** (#293): dieselbe Auswahl wie die Zeilen (`getItemProps`), samt
    der frisch angelegten Aufgabe, die schon im ersten Bild markiert dasteht.
  */
  const hullSelectedIds = useMemo(() => {
    const base = externalSelection?.selectedIds ?? selectedIds
    if (!autoSelectId || base.has(autoSelectId) || !taskIds.includes(autoSelectId)) return base
    return new Set([...base, autoSelectId])
  }, [externalSelection?.selectedIds, selectedIds, autoSelectId, taskIds])
  /*
    **Mehrfachauswahl, ohne jede Zeile zu fragen** (#303): Als Prop kippte der Wechsel von 1 auf 2 alle Zeilen
    der Liste in ein neues Rendern (bei 150 Zeilen ein langer Commit). Die Liste trägt die Klasse, Menü und
    Verschiebe-Kasten fragen beim Öffnen über eine Funktion, die sich nie ändert.
  */
  const multiSelected = (externalSelection?.selectedIds ?? selectedIds).size > 1
  const multiSelectedRef = useRef(multiSelected)
  multiSelectedRef.current = multiSelected
  const isMultiSelected = useCallback(() => multiSelectedRef.current, [])
  const dormantIds = useMemo(
    () => new Set(filteredTasks.filter(t => t.dormant).map(t => t.id)),
    [filteredTasks],
  )
  // Beim Ziehen und Landen ruht die Hülle, die Zeilen malen sich selbst (`useRestAfterDrop`). Ruht die
  // Hülle der ganzen Seite (Abschnitt gezogen, `SelectionHullScope`), ebenso.
  const scopeResting = useSelectionHullResting()
  const hullResting = useRestAfterDrop(!!activeDragId)

  // Map task ID → interaction index (excludes exiting tasks)
  const taskIdxMap = useMemo(() => {
    const map = new Map<string, number>()
    taskIds.forEach((id, i) => map.set(id, i))
    return map
  }, [taskIds])

  // IDs for SortableContext (only active, non-exiting tasks)
  const sortableIds = useMemo(
    () => displayList.filter(d => !d.exiting).map(d => d.task.id),
    [displayList],
  )

  // ── Scroll inline creator into view when it opens or moves ──
  const inlineCreatorRef = useRef<HTMLDivElement>(null)
  const prevInlineAfterRef = useRef<string | null>(null)
  useEffect(() => {
    const afterId = inlineCreation?.afterTaskId ?? null
    if (afterId && afterId !== prevInlineAfterRef.current) {
      const timer = setTimeout(() => {
        inlineCreatorRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }, 300) // after mount animation (250ms)
      prevInlineAfterRef.current = afterId
      return () => clearTimeout(timer)
    }
    if (!afterId) prevInlineAfterRef.current = null
    return undefined
  }, [inlineCreation?.afterTaskId])

  if (filteredTasks.length === 0 && !hasExiting && !inlineCreation) return null

  // ── Build inline creator element(s) (reused in flatMap + fallback) ──
  const isMorphing = !!inlineCreation?.morphTitle
  const isTwoElementMorph = isMorphing && inlineCreation?.morphKey != null

  // Morph element: separate collapsed entry during continue-morph. Uses the SAME
  // wrapper key as the previous creator so React reuses the component instance
  // and the inner TaskEntry transitions smoothly from creating→collapsed.
  const inlineMorphElement = isTwoElementMorph ? (
    <motion.div
      key={`inline-wrap-${inlineCreation.morphKey}`}
      initial={false}
      animate={{ opacity: 1, height: 'auto' }}
    >
      <TaskEntry
        key={`inline-create-${inlineCreation.morphKey}`}
        mode="collapsed"
        task={{ id: 'inline-morphing', title: inlineCreation.morphTitle!, completed: false }}
        selected={inlineCreation.morphSelected ?? false}
        onMorphComplete={inlineCreation.onMorphComplete}
      />
    </motion.div>
  ) : null

  // Creator element: the main inline creator (empty input or single-element morph).
  const inlineCreatorEntry = inlineCreation ? (
    <TaskEntry
      key={`inline-create-${inlineCreation.key}`}
      openTintFrom={inlineCreation.landed ? 'selected' : undefined}
      mode={(!isTwoElementMorph && inlineCreation.morphTitle) ? 'collapsed' : 'creating'}
      task={(!isTwoElementMorph && inlineCreation.morphTitle) ? { id: 'inline-morphing', title: inlineCreation.morphTitle, completed: false } : undefined}
      selected={(!isTwoElementMorph && inlineCreation.morphTitle) ? (inlineCreation.morphSelected ?? false) : false}
      resetKey={inlineCreation.resetKey}
      onSubmitCreate={(!isMorphing || isTwoElementMorph) ? inlineCreation.onSubmitCreate : undefined}
      onSubmitCreateAndContinue={(!isMorphing || isTwoElementMorph) ? inlineCreation.onSubmitCreateAndContinue : undefined}
      onCancel={(!isMorphing || isTwoElementMorph) ? inlineCreation.onCancel : undefined}
      autoFocus={(!isMorphing || isTwoElementMorph)}
      initialExpanded={!!inlineCreation.skipEntrance}
      onMorphComplete={(!isTwoElementMorph && inlineCreation.morphTitle) ? inlineCreation.onMorphComplete : undefined}
    />
  ) : null

  const inlineCreatorElement = inlineCreation ? (
    <motion.div
      ref={inlineCreatorRef}
      key={`inline-wrap-${inlineCreation.key}`}
      // Gelandet aus dem Zug (#301): sofort an der Stelle der Lücke — die Seite lässt sie von der Fläche her gleiten.
      data-new-task-landing={inlineCreation.landed ? '' : undefined}
      initial={(isMorphing && !isTwoElementMorph) || inlineCreation.skipEntrance || inlineCreation.landed ? false : { opacity: 0, height: 0, overflow: 'hidden' as const }}
      animate={{ opacity: 1, height: 'auto', transitionEnd: { overflow: 'visible' as const } }}
      transition={inlineCreation.skipEntrance
        ? { duration: 0 }
        : {
            height: { duration: 0.25, ease: [0.4, 0, 0.2, 1] },
            opacity: { duration: 0.2, delay: 0.05 },
          }
      }
    >
      {inlineCreatorEntry}
    </motion.div>
  ) : null

  const listContent = (() => {
          let inlineCreatorRendered = false
          const items = displayList.flatMap(({ task, exiting }) => {
            if (exiting) {
              return (
                <motion.div
                  key={task.id}
                  data-task-id={task.id}
                  layout={false}
                  initial={false}
                  animate={{ height: 0 }}
                  transition={{ height: { duration: 0.2, delay: 0.05, ease: [0.4, 0, 0.2, 1] } }}
                  style={{ overflow: 'hidden', pointerEvents: 'none' }}
                >
                  <motion.div
                    initial={false}
                    animate={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <TaskEntry
                      mode="collapsed"
                      task={task}
                      selected={false}
                      focused={false}
                      onToggleComplete={() => {}}
                      checkKind={checkKind}
                      checkVariant={checkVariant}
                    />
                  </motion.div>
                </motion.div>
              )
            }

            // Eine festgehaltene Zeile hat keinen Platz mehr in der Auswahl: -1 wählt nichts.
            const idx = heldIds?.has(task.id) ? -1 : taskIdxMap.get(task.id) ?? 0
            const isDraggedGroup = !!draggedIds && draggedIds.has(task.id) && task.id !== activeDragId
            const isEntering = enteringIdsRef.current.has(task.id)
            const sortableEntry = (
              <SortableTaskEntry
                key={isEntering ? undefined : task.id}
                sortableId={task.id}
                isDraggedGroup={isDraggedGroup}
                dragCount={task.id === activeDragId ? dragCount : undefined}
                isOverlayActive={task.id === activeDragId}
                isCrossSegmentDrag={isCrossSegmentDrag}
                isDragActive={!!activeDragId}
                nestState={nestTarget?.id === task.id ? (nestTarget.blocked ? 'blocked' : 'armed') : undefined}
                mode={editingTaskId === task.id ? 'expanded' : 'collapsed'}
                task={task}
                {...getItemProps(idx)}
                selectionHull={!hullResting && !scopeResting}
                onToggleComplete={handleItemToggleComplete}
                onDelete={onDelete ? handleItemDelete : undefined}
                onDuplicate={onDuplicate ? handleItemDuplicate : undefined}
                onContextMenuBadgeUpdate={onContextMenuBadgeUpdate}
                getContextMenuMarks={getContextMenuMarks}
                menuShortcut={menuShortcut}
                onMenuShortcutClose={onMenuShortcutClose}
                onClick={handleSelect}
                onDoubleClick={handleDoubleClick}
                onSubmitEdit={handleSubmitEdit}
                onSubmitEditAndCreateBelow={handleSubmitEditAndCreateBelow}
                onNavigateNext={editingTaskId === task.id ? () => onNavigateEdit?.(task.id, 'next') : undefined}
                onNavigatePrev={editingTaskId === task.id ? () => onNavigateEdit?.(task.id, 'prev') : undefined}
                onCancel={handleCancelEdit}
                checkKind={checkKind}
                checkVariant={checkVariant}
                menuKind={menuKind}
                onShowInProject={onShowInProject}
                onReopen={onReopen}
                projectBadgePlacement={projectBadgePlacement}
                recurrenceDisplay={recurrenceDisplay}
                hidePlay={hidePlay}
                onPlay={onPlay}
                initialExpanded={task.id === autoCollapsingTaskId}
                autoFocus={task.id !== autoCollapsingTaskId}
                inProjectContext={inProjectContext}
              />
            )

            let entry: React.ReactNode
            if (isEntering) {
              entry = <EnteringRow key={task.id}>{sortableEntry}</EnteringRow>
            } else {
              entry = sortableEntry
            }

            // Beim Ziehen von „Neue Aufgabe“ (#301) steht hinter jeder Zeile eine Lücke bereit.
            if (newTaskGap) {
              return [entry, (
                <NewTaskDropGap
                  key={`new-task-gap-${task.id}`}
                  open={newTaskGap.afterTaskId === task.id}
                  height={newTaskGap.height}
                  flexGap={LIST_ROW_GAP}
                />
              )]
            }

            // Insert inline creator (+ optional morph element) right after the target task
            if (inlineCreation && task.id === inlineCreation.afterTaskId) {
              inlineCreatorRendered = true
              return inlineMorphElement
                ? [entry, inlineMorphElement, inlineCreatorElement]
                : [entry, inlineCreatorElement]
            }

            return entry
          })

          // Fallback: if the inline target task hasn't appeared in the list yet
          // (useLiveQuery async gap after Cmd+Enter), render creator at the end.
          if (inlineCreation && !inlineCreatorRendered && inlineCreatorElement) {
            items.push(inlineCreatorElement)
          }

          return items
        })()

  const list = (
    <MultiSelectionContext.Provider value={isMultiSelected}>
    <TaskRowDataScope taskIds={taskIds}>
    <LazyRowScope>
    <div className={`task-list${multiSelected ? ' task-list--multi-selected' : ''}`} {...listProps}>
      <SelectionHull
        rowIds={taskIds}
        selectedIds={hullSelectedIds}
        dormantIds={dormantIds}
        excludedId={editingTaskId}
        rowAttribute="data-task-id"
        rowSelector=".task-entry"
        suspended={hullResting}
      />
      {skipSortableContext
        ? listContent
        : <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>{listContent}</SortableContext>
      }
    </div>
    </LazyRowScope>
    </TaskRowDataScope>
    </MultiSelectionContext.Provider>
  )
  // Verwaltet die Seite die Tasten (`externalSelection`: `TaskBoard`, Geschafft), gehört der Fokus
  // ihrem Wrapper — dessen Anbieter gilt dann auch hier (#284). Ein Wächtertest (`listFocusWiring`)
  // hält fest, dass jeder solche Wrapper einen stellt.
  return externalSelection ? list : <ListFocusContext.Provider value={restoreListFocus}>{list}</ListFocusContext.Provider>
}
